import { adminConfirmation, formatAdminDate } from '../data/guide/adminConfirmations'
import { EXPORT_DATE_LABEL } from '../data/world/package'
import { coordLabel, nameKey } from './worldFormat'
import type { Npc, NpcReference } from '../types/progression'
import type { Spawn, WorldData } from '../types/world'

/**
 * Resolución ÚNICA de la ubicación de un NPC. La usan la tarjeta del NPC, las misiones, las recetas,
 * el buscador y el panel del staff, para que nunca muestren estados distintos.
 *
 * Orden:
 * 1. Confirmado por administración (no depende de los archivos).
 * 2. Archivos aún cargando / error de carga → nunca se interpretan como "sin ubicación".
 * 3. Apariciones PERMANENTES configuradas del mismo monster_id (no invasiones ni eventos).
 * 4. Referencia clásica u oficial con su fuente → "Pendiente de validar en Vanguard".
 * 5. Ubicación no documentada.
 */
export type LocationPoint = { mapName: string; mapId?: number; coords?: string }

export type NpcLocation =
  | { state: 'admin'; points: LocationPoint[]; condition?: string; date: string; answer: string }
  | { state: 'loading'; reference?: ResolvedReference }
  | { state: 'error'; message?: string; reference?: ResolvedReference }
  | { state: 'config'; points: LocationPoint[]; spawns: Spawn[]; date: string; monsterId: number }
  | { state: 'reference'; reference: ResolvedReference }
  | { state: 'none' }

export type ResolvedReference = Omit<NpcReference, 'points'> & { points: LocationPoint[] }

export type WorldSnapshot = { status: string; data?: WorldData; error?: string }

/** Mapa por nombre: exacto, o parcial solo si hay un único candidato ("Barracks" → "Barracks of Balgass"). */
export function findMapByName(w: WorldData | undefined, name: string): { id: number; name: string } | undefined {
  if (!w) return undefined
  const k = nameKey(name)
  const list = Array.from(w.maps.values())
  const exact = list.find((m) => nameKey(m.name) === k)
  if (exact) return { id: exact.id, name: exact.name }
  const partial = list.filter((m) => nameKey(m.name).split(' ').includes(k))
  return partial.length === 1 ? { id: partial[0].id, name: partial[0].name } : undefined
}

const xy = (x?: number, y?: number) => (x !== undefined && y !== undefined ? `X: ${x}, Y: ${y}` : undefined)

const toPoint = (w: WorldData | undefined, p: { mapName: string; x?: number; y?: number }): LocationPoint => {
  const m = findMapByName(w, p.mapName)
  return { mapName: m?.name ?? p.mapName, mapId: m?.id, coords: xy(p.x, p.y) }
}

export const isPermanentSpawn = (s: Spawn) => !s.eventId && s.kind !== 'invasion' && s.kind !== 'event'

export const npcAdminKey = (npc: Npc) => `npc-location:${npc.monsterId ?? npc.id}`

export function resolveNpcLocation(npc: Npc, world: WorldSnapshot): NpcLocation {
  const w = world.data
  const admin = adminConfirmation(npcAdminKey(npc))
  const adminPoints = admin ? [...(admin.locations ?? []), ...(admin.location ? [admin.location] : [])] : []
  if (admin && adminPoints.length) {
    return { state: 'admin', points: adminPoints.map((p) => toPoint(w, p)), condition: admin.condition, date: formatAdminDate(admin.date), answer: admin.answer }
  }
  const reference = npc.reference ? { ...npc.reference, points: npc.reference.points.map((p) => toPoint(w, p)) } : undefined
  if (world.status === 'error') return { state: 'error', message: world.error, reference }
  if (!w) return { state: 'loading', reference }
  // Solo el NPC cuya función está confirmada: un candidato (p. ej. Pet Trainer 226/234) no aporta ubicación.
  if (npc.monsterId !== undefined) {
    const spawns = (w.idx.spawnsByMonster.get(npc.monsterId) ?? []).filter(isPermanentSpawn)
    if (spawns.length) {
      return {
        state: 'config',
        spawns,
        monsterId: npc.monsterId,
        date: EXPORT_DATE_LABEL,
        points: spawns.map((s) => ({ mapName: w.maps.get(s.mapId)?.name ?? `Mapa ${s.mapId}`, mapId: s.mapId, coords: coordLabel(s) })),
      }
    }
  }
  if (reference) return { state: 'reference', reference }
  return { state: 'none' }
}

const pointText = (p: LocationPoint) => `${p.mapName}${p.coords ? ` — ${p.coords}` : ''}`

/** Texto breve y copiable de la ubicación resuelta (buscador, copiar, panel). */
export function npcLocationText(loc: NpcLocation): string {
  switch (loc.state) {
    case 'admin':
      return `${loc.points.map(pointText).join(' · ')} (confirmado por administración, ${loc.date})`
    case 'config':
      return `${loc.points.map(pointText).join(' · ')} (ubicación documentada en la configuración del ${loc.date})`
    case 'reference':
      return `Ubicación de referencia: ${loc.reference.points.map(pointText).join(' · ')} (pendiente de validar en Vanguard)`
    case 'loading':
      return 'Cargando ubicación…'
    case 'error':
      return 'No se pudo comprobar la ubicación: error de carga de los archivos'
    default:
      return 'Ubicación no documentada'
  }
}

export const npcLocationValidation = (loc: NpcLocation): { status: 'documentado' | 'admin' | 'clasica' | 'pendiente'; detail: string } => {
  switch (loc.state) {
    case 'config':
      return { status: 'documentado', detail: npcLocationText(loc) }
    case 'admin':
      return { status: 'admin', detail: npcLocationText(loc) }
    case 'reference':
      return { status: 'clasica', detail: npcLocationText(loc) }
    case 'loading':
      return { status: 'pendiente', detail: 'Cargando datos…' }
    case 'error':
      return { status: 'pendiente', detail: 'Error de carga: la ubicación no se pudo comprobar' }
    default:
      return { status: 'pendiente', detail: 'Ubicación no documentada' }
  }
}
